import PageHeader from "@/components/PageHeader";
import { CtaBanner } from "@/components/CTA";
import { Section } from "@/pages/Home";
import Seo from "@/components/Seo";
import { IMG, ImageBreak } from "@/components/ImageAssets";
import WhereDiagram from "@/components/WhereDiagram";
import { motion } from "framer-motion";
import {
  AlertTriangle, PhoneOff, Users, TrendingDown, LinkIcon, Unlink,
  UserCircle2, ClipboardCheck, ArrowRight, Radar, RefreshCcw, Target,
  CheckCircle2, ShieldAlert, ExternalLink,
  Users as UsersIcon, Activity as ActivityIcon, Compass, ShieldCheck, ArrowUpRight,
  BookOpen, HeartHandshake, Briefcase, Database, Globe, GraduationCap, Sparkles,
  Network, Lightbulb, Route, Bell,
  MessagesSquare, BarChart3, UserPlus,
} from "lucide-react";

// ---- Verified statistics (December 2025) ----
// All figures are drawn from GAO, DoD, VA, HUD, and U.S. Census public reports.
// If a figure cannot be verified, the label reads "Verified statistic needed."
const stats = [
  {
    value: "~200,000",
    label: "Service members transition each year",
    body: "Approximately 200,000 U.S. service members separate from the military and enter civilian life annually.",
    source: "U.S. GAO · Helping Servicemembers Transition Back to Civilian Life",
    href: "https://www.gao.gov/helping-servicemembers-transition-back-to-civilian-life",
  },
  {
    value: "41,000",
    label: "Warm handovers provided by DOD",
    body: "From April 2021 to March 2023, DOD provided at least one warm handover to about 41,000 service members through the Transition Assistance Program.",
    source: "GAO-24-106248 · Agencies Can Improve Warm Handovers (Mar 2024)",
    href: "https://www.gao.gov/products/gao-24-106248",
  },
  {
    value: "4,300+",
    label: "At-risk members who did NOT receive a warm handover",
    body: "In that same period, more than 4,300 at-risk service members did not receive a warm handover — DOD had not analyzed why.",
    source: "GAO-24-106248 · Agencies Can Improve Warm Handovers (Mar 2024)",
    href: "https://www.gao.gov/products/gao-24-106248",
  },
  {
    value: "17.9M",
    label: "Living U.S. veterans (FY 2024)",
    body: "The Department of Veterans Affairs reports approximately 17.9 million living veterans as of Fiscal Year 2024.",
    source: "U.S. Department of Veterans Affairs · VetPop / National Center for Veterans Analysis and Statistics",
    href: "https://www.va.gov/vetdata/veteran_population.asp",
  },
  {
    value: "56%",
    label: "VA Solid Start response rate (Year 1)",
    body: "VA Solid Start reached recently separated veterans by phone at a 56% response rate in its first year — nearly quadruple its original 15% target.",
    source: "GAO-23-105699 · Transitioning Servicemembers",
    href: "https://www.gao.gov/products/gao-23-105699",
  },
  {
    value: "32,882",
    label: "Veterans experiencing homelessness (2024 PIT)",
    body: "HUD's 2024 Point-in-Time count identified 32,882 veterans experiencing homelessness — an 8% decrease from 2023, and a reminder of the human cost of gaps in care.",
    source: "HUD · 2024 Annual Homeless Assessment Report (Part 1, PIT Estimates)",
    href: "https://www.huduser.gov/portal/datasets/ahar/2024-ahar-part-1-pit-estimates-of-homelessness-in-the-us.html",
  },
];

const problemCards = [
  {
    icon: <Unlink className="w-6 h-6" />,
    title: "Fragmented systems, not fragmented people",
    body: "DoD, VA, workforce boards, healthcare systems, universities, and community nonprofits each serve pieces of the transition. The systems do not always talk to each other.",
    source: "GAO · Helping Servicemembers Transition Back to Civilian Life",
  },
  {
    icon: <PhoneOff className="w-6 h-6" />,
    title: "Referrals without accountability",
    body: "A phone number, a brochure, or a website is not a connection. Most systems track referrals made — very few confirm whether the person was actually helped.",
    source: "GAO · Military and Veteran Support (GAO-24-107752)",
  },
  {
    icon: <TrendingDown className="w-6 h-6" />,
    title: "Preparation is not the same as connection",
    body: "DoD TAP provides essential preparation, but preparation alone does not guarantee a warm connection to post-transition benefits, healthcare, or community services.",
    source: "DoD Transition Assistance Program · dodtap.mil",
  },
  {
    icon: <ShieldAlert className="w-6 h-6" />,
    title: "Risk concentrates in the first year",
    body: "The 12 months after separation carry heightened risk for mental health crises, housing instability, and disengagement — the exact moment VA Solid Start was created to address.",
    source: "VA · Solid Start",
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Families carry the load",
    body: "Spouses, parents, and children shoulder the invisible work of navigating benefits, healthcare, childcare, and school transitions across multiple systems.",
    source: "VA · Transition Resources",
  },
  {
    icon: <RefreshCcw className="w-6 h-6" />,
    title: "No closed loop = no learning",
    body: "Without outcome tracking, agencies and funders can't see which handoffs work, which don't, and where the community's next gap is emerging.",
    source: "GAO · DoD/VA coordination findings",
  },
];

const flowStages = [
  { icon: <UserCircle2 className="w-5 h-5" />, label: "Separation from service", body: "The member leaves active duty and civilian life begins." },
  { icon: <ClipboardCheck className="w-5 h-5" />, label: "TAP · initial counseling", body: "Preparation begins — but often late, and preparation is not connection." },
  { icon: <ArrowRight className="w-5 h-5" />, label: "Referral to services", body: "Benefits, employment, healthcare, housing, education — separate systems, separate handoffs." },
  { icon: <Unlink className="w-5 h-5" />, label: "Loss of connection", body: "No confirmation. No follow-up. No outcome tracked. The person is left between systems." },
  { icon: <AlertTriangle className="w-5 h-5" />, label: "Crisis · delay · duplication", body: "A missed handoff becomes a missed appointment, a lapsed benefit, or a mental-health crisis." },
];

const traditional = [
  "Person receives a phone number, website, or program name.",
  "No confirmed connection.",
  "No shared accountability.",
  "No outcome tracking.",
  "The burden stays on the veteran or family.",
];

const warmHandoff = [
  "Need is identified.",
  "Provider match is verified.",
  "Connection is confirmed.",
  "Follow-up is documented.",
  "Outcome is measured.",
  "The person is not left alone between systems.",
];

const sources = [
  { name: "GAO-24-106248 · Service Members Transitioning to Civilian Life: Agencies Can Improve Warm Handovers", href: "https://www.gao.gov/products/gao-24-106248" },
  { name: "GAO · Helping Servicemembers Transition Back to Civilian Life", href: "https://www.gao.gov/helping-servicemembers-transition-back-to-civilian-life" },
  { name: "GAO-24-107752 · Military and Veteran Support", href: "https://www.gao.gov/products/gao-24-107752" },
  { name: "GAO-19-438R · Transitioning Service Members", href: "https://www.gao.gov/products/gao-19-438r" },
  { name: "DoD Transition Assistance Program", href: "https://www.dodtap.mil/" },
  { name: "VA Transition Assistance Program", href: "https://benefits.va.gov/transition/" },
  { name: "VA Solid Start", href: "https://error.vba.va.gov/BENEFITS/solid-start.asp" },
  { name: "VA · National Center for Veterans Analysis and Statistics (VetPop)", href: "https://www.va.gov/vetdata/veteran_population.asp" },
  { name: "HUD · 2024 Annual Homeless Assessment Report (PIT)", href: "https://www.huduser.gov/portal/datasets/ahar/2024-ahar-part-1-pit-estimates-of-homelessness-in-the-us.html" },
  { name: "AEI · Sustaining a National Treasure: Veteran Transitions and the Life-Cycle of the All-Volunteer Force", href: "https://www.aei.org/research-products/report/sustaining-a-national-treasure-veteran-transitions-and-the-life-cycle-of-the-all-volunteer-force/" },
  { name: "RAND · Policy Recommendations to Improve Transition Support for Those Leaving the Military (CF-A1363-7)", href: "https://www.rand.org/pubs/conf_proceedings/CFA1363-7.html" },
  { name: "D'Aniello Institute for Veterans and Military Families (IVMF) · Syracuse University", href: "https://ivmf.syracuse.edu/" },
];

export default function Problem() {
  return (
    <div data-testid="problem-page">
      <Seo
        title="The Problem | WHO — Warm Hand Off · The Accountability Gap"
        description="~200,000 U.S. service members transition each year (DoD TAP). Programs exist. The gap is accountable connection &mdash; the loop that WHO Guides™ close. Research-backed with GAO, DoD, VA, HUD, RAND, and Penn State sources."
        path="/problem"
      />
      <PageHeader
        eyebrow="The Problem"
        title="The gap is not resources. The gap is connection."
        lede="Every year, roughly 200,000 U.S. service members transition from military to civilian life (DoD TAP). Federal, state, and community programs exist across benefits, healthcare, employment, housing, education, and mental wellness &mdash; but there is no accountable system to confirm the person actually connects, is served, and reaches a measurable outcome. That is the gap WHO Guides&trade; close."
      />

      {/* ANIMATED — Where We Are (SVG diagram, comes alive on scroll) */}
      <WhereDiagram variant="problem" />

      {/* ANIMATED — Where WHO Fits (solution companion) */}
      <WhereDiagram variant="solution" />

      {/* Short intro */}
      <Section eyebrow="Zoom in" title="Real programs. Missing coordination.">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-5 text-lg text-slateSoft leading-relaxed">
            <p>
              Transition is not a single event. It is an extended process across employment, benefits,
              healthcare, disability, housing, education, family adjustment, mental wellness, and identity
              change — each managed by a different organization with different rules and different data.
            </p>
            <p>
              The DoD Transition Assistance Program (TAP) provides essential preparation. VA Solid Start
              reaches out in the first year after separation. Community providers, nonprofits, workforce
              boards, and healthcare systems each deliver real services. But GAO has repeatedly identified
              opportunities for DoD and VA to improve coordination, data analysis, and program effectiveness
              across those transition supports.
            </p>
            <p className="text-navy font-medium">
              For many veterans and families, the challenge is not whether help exists. The challenge is
              finding the right help, making contact, staying connected, and knowing whether the handoff
              actually worked.
            </p>
          </div>
          <aside className="lg:col-span-4 p-7 bg-cream-panel border-l-4 border-gold rounded-md" data-testid="problem-callout">
            <p className="eyebrow text-gold-deep">The real problem</p>
            <p className="mt-4 font-display font-bold text-navy text-xl leading-snug">
              Referrals without accountability leave transitioning service members and their families to
              navigate fragmented systems alone.
            </p>
            <p className="mt-4 text-sm text-slateSoft">
              Sources: GAO, DoD TAP, VA Transition Resources.
            </p>
          </aside>
        </div>
      </Section>

      {/* Three-column problem cards */}
      <Section eyebrow="What's actually broken" title="Six structural gaps in the transition ecosystem." bg="cream">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problemCards.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.5 }}
              className="bg-white border border-borderSubtle rounded-lg p-7 hover:shadow-md transition-shadow"
              data-testid={`problem-card-${i}`}
            >
              <span className="inline-flex w-11 h-11 items-center justify-center rounded-md bg-navy text-gold">
                {c.icon}
              </span>
              <h3 className="mt-5 font-display font-bold text-navy text-lg leading-tight">{c.title}</h3>
              <p className="mt-3 text-slateSoft leading-relaxed">{c.body}</p>
              <p className="mt-5 pt-4 border-t border-borderSubtle text-xs uppercase tracking-widest text-slateSoft/80" data-testid={`problem-card-${i}-source`}>
                Source · {c.source}
              </p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Image break — humanize the data */}
      <ImageBreak
        src={IMG.militaryFamily}
        alt="A veteran embraced by their family"
        eyebrow="Behind every referral"
        caption="A person. A family. A future waiting on the connection."
        testId="problem-image-break-1"
      />

      {/* By the Numbers */}
      <section className="py-24 lg:py-32 hero-gradient text-cream" data-testid="stat-band">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold-light">By the Numbers</p>
            <h2 className="mt-4 font-display font-black tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl">
              The scale of the transition — and the gap.
            </h2>
            <p className="mt-5 text-lg text-cream/80 leading-relaxed">
              Every statistic below is drawn from a verified U.S. Government Accountability Office, DoD, VA,
              HUD, or Census public report. Links are included for direct verification.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="p-7 border border-white/10 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
                data-testid={`stat-block-${i}`}
              >
                <div className="font-display font-black text-gold text-5xl md:text-6xl leading-none tracking-tight" data-testid={`stat-value-${i}`}>
                  {s.value}
                </div>
                <p className="mt-4 font-display font-bold text-cream text-lg leading-tight">{s.label}</p>
                <p className="mt-3 text-cream/75 leading-relaxed text-[15px]">{s.body}</p>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`stat-source-${i}`}
                  className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold-light hover:text-gold transition-colors"
                >
                  Source · {s.source} <ExternalLink className="w-3 h-3" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Where the system breaks down (flow diagram) */}
      <Section eyebrow="Where the transition system breaks down" title="Five stages. One missing loop.">
        <p className="text-lg text-slateSoft leading-relaxed max-w-3xl mb-14">
          Transitioning service members and veterans are not usually failed by a lack of programs — they are
          failed by disconnected handoffs between programs. Here is how the loop breaks today.
        </p>

        <ol className="relative border-l-2 border-dashed border-gold/50 pl-8 space-y-8" data-testid="flow-diagram">
          {flowStages.map((s, i) => (
            <motion.li
              key={s.label}
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="relative"
              data-testid={`flow-stage-${i}`}
            >
              <span className="absolute -left-[43px] top-1 inline-flex w-10 h-10 items-center justify-center rounded-full bg-navy text-gold ring-4 ring-cream">
                {s.icon}
              </span>
              <div className="p-6 bg-white border border-borderSubtle rounded-lg">
                <p className="eyebrow text-gold-deep">Stage {String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-2 font-display font-bold text-navy text-lg">{s.label}</h3>
                <p className="mt-2 text-slateSoft leading-relaxed">{s.body}</p>
              </div>
            </motion.li>
          ))}

          {/* WHO repair point */}
          <motion.li
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: flowStages.length * 0.1, duration: 0.5 }}
            className="relative"
            data-testid="flow-stage-who"
          >
            <span className="absolute -left-[43px] top-1 inline-flex w-10 h-10 items-center justify-center rounded-full bg-gold text-navy ring-4 ring-cream">
              <LinkIcon className="w-5 h-5" />
            </span>
            <div className="p-6 bg-navy text-cream rounded-lg">
              <p className="eyebrow text-gold-light">WHO Guides · The repair point</p>
              <h3 className="mt-2 font-display font-bold text-cream text-lg">A WHO Guide closes the loop.</h3>
              <p className="mt-2 text-cream/80 leading-relaxed">
                A person speaks. A real WHO Guide, supported by nine specialist AI agents,
                coordinates verified partners, secures consent, confirms the handoff, and follows
                through &mdash; so no one is left alone between the referral and the connection.
              </p>
            </div>
          </motion.li>
        </ol>
        <p className="mt-8 text-xs uppercase tracking-widest text-slateSoft">
          Source notes · GAO reports on DoD/VA transition programs, DoD TAP, VA Solid Start.
        </p>
      </Section>

      {/* Independent Research Supports the Need for WHO — RAND callout */}
      <section
        className="relative py-24 lg:py-32 bg-navy-deep text-cream border-y border-white/5 overflow-hidden"
        data-testid="rand-independent-research-section"
        id="independent-research"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-gold/8 blur-3xl" />
          <div className="absolute inset-0 dot-grid opacity-[0.06]" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold" data-testid="rand-eyebrow">
              Independent Research Supports the Need for WHO
            </p>
            <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.2rem]">
              RAND identified the policy need.<br />
              <span className="text-gold">WHO is building the coordination infrastructure.</span>
            </h2>
            <p className="mt-6 text-lg text-cream/85 leading-relaxed">
              A RAND Corporation policy roundtable found that military and veteran transition programs
              are numerous but often insufficiently coordinated. Experts called for stronger
              integration among government agencies, nonprofit organizations, and community providers;
              proactive warm handoffs; easier access to trusted resources; greater family involvement;
              consistent outcome measurement; and better identification of service gaps.
            </p>
            <p className="mt-4 text-cream/75 leading-relaxed">
              WHO is being designed to help make these recommendations operational &mdash; by
              transforming fragmented referrals into accountable, measurable, human-centered
              connections.
            </p>
          </div>

          {/* Research → Response comparison cards */}
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rand-comparison-grid">
            {[
              {
                icon: <Radar className="w-4 h-4" />,
                finding: "Programs lack coordination",
                response: "WHO provides a shared coordination environment for participating organizations.",
              },
              {
                icon: <Compass className="w-4 h-4" />,
                finding: "Veterans struggle to identify the right resources",
                response: "WHO uses verified resources, location, eligibility, and individual needs to guide users toward appropriate support.",
              },
              {
                icon: <LinkIcon className="w-4 h-4" />,
                finding: "Experts recommend proactive warm handoffs",
                response: "WHO enables direct introductions, referral acceptance, ownership, follow-up, and closed-loop communication.",
              },
              {
                icon: <UsersIcon className="w-4 h-4" />,
                finding: "Military families need to be included",
                response: "WHO supports transitioning service members, veterans, spouses, caregivers, and families as part of one connected transition journey.",
              },
              {
                icon: <ActivityIcon className="w-4 h-4" />,
                finding: "Programs need stronger outcome measurement",
                response: "WHO provides referral tracking, partner dashboards, service-gap identification, and measurable outcomes.",
              },
              {
                icon: <ShieldCheck className="w-4 h-4" />,
                finding: "Programs need to be trustworthy at the moment of handoff",
                response: "WHO enforces verified provider status, consent-first data sharing, and role-based access &mdash; so trust is engineered in, not assumed.",
              },
            ].map((c, i) => (
              <motion.div
                key={c.finding}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
                className="p-6 bg-white/[0.04] border border-white/10 hover:border-gold/40 rounded-lg transition-colors"
                data-testid={`rand-card-${i}`}
              >
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-gold/15 border border-gold/40 text-gold">
                    {c.icon}
                  </span>
                  <p className="text-[10px] uppercase tracking-widest text-gold font-bold">RAND finding</p>
                </div>
                <p className="mt-3 font-display font-bold text-cream text-base leading-tight">
                  {c.finding}
                </p>
                <div className="my-4 h-[1px] bg-white/10" />
                <p className="text-[10px] uppercase tracking-widest text-gold-light font-bold">WHO response</p>
                <p className="mt-2 text-cream/80 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: c.response }} />
              </motion.div>
            ))}
          </div>

          {/* Highlighted callout */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mt-14 rounded-2xl p-8 lg:p-10 bg-gradient-to-br from-gold/15 to-gold/5 border border-gold/40 text-center"
            data-testid="rand-callout"
          >
            <p className="font-display font-black text-cream text-3xl lg:text-4xl leading-tight">
              Programs exist. <span className="text-gold">Accountability between them does not.</span>
            </p>
            <p className="mt-4 text-cream/85 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
              RAND recommends making resources easier to find. <span className="text-cream font-semibold">
              WHO helps ensure the connection is completed.</span>
            </p>
          </motion.div>

          {/* Disclaimer, citation, CTA */}
          <div className="mt-10 pt-8 border-t border-white/10 grid md:grid-cols-2 gap-6 items-start">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gold-light font-bold">Disclosure</p>
              <p className="mt-2 text-xs text-cream/60 leading-relaxed">
                The RAND research supports the need for improved transition coordination. RAND did not
                evaluate or endorse WHO. This section reflects WHO&apos;s own response to publicly
                available policy findings.
              </p>
              <p className="mt-4 text-[10px] uppercase tracking-widest text-gold-light font-bold">Source</p>
              <p className="mt-2 text-xs text-cream/60 leading-relaxed" data-testid="rand-citation">
                RAND Corporation, <em>Policy Recommendations to Improve Transition Support for Those
                Leaving the Military: Proceedings from a Roundtable and Perspectives from Experts</em>
                &nbsp;(CF-A1363-7), RAND Epstein Family Veterans Policy Research Institute, 2024.
              </p>
            </div>
            <div className="md:text-right">
              <a
                href="https://www.rand.org/pubs/conf_proceedings/CFA1363-7.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rand-explore-btn"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                Explore the Research
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </a>
              <p className="mt-3 text-[10px] uppercase tracking-widest text-cream/40">Opens on rand.org</p>
            </div>
          </div>
        </div>
      </section>

      {/* Independent Research — Penn State Veteran Transition Screener */}
      <section
        className="relative py-24 lg:py-32 bg-cream text-navy border-b border-borderSubtle overflow-hidden"
        data-testid="pennstate-research-section"
        id="pennstate-research"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-navy/[0.04] blur-3xl" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold-deep" data-testid="pennstate-eyebrow">
              The Right Connection Begins with Understanding the Whole Person
            </p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.2rem]">
              Research can identify the need.<br />
              <span className="text-gold-deep">WHO helps complete the connection.</span>
            </h2>
            <p className="mt-6 text-lg text-slateSoft leading-relaxed">
              Veteran transition is not a single event or a one-size-fits-all experience. A veteran
              may simultaneously face challenges involving employment, education, finances, housing,
              physical health, mental health, family adjustment, and social connection.
            </p>
            <p className="mt-4 text-slateSoft leading-relaxed">
              Researchers affiliated with Penn State developed the <em>Veteran Transition Screener</em>
              to help organizations better understand these interconnected needs and provide more
              individualized recommendations.
            </p>
            <p className="mt-4 text-slateSoft leading-relaxed">
              WHO is being designed to serve as the <span className="text-navy font-semibold">coordination
              layer that follows assessment</span>. Once a need is identified, WHO helps determine the
              appropriate pathway, connect the veteran or family member to a verified provider,
              establish referral ownership, track follow-up, and confirm whether meaningful support
              was received.
            </p>
          </div>

          {/* Four-step pathway */}
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pennstate-pathway">
            {[
              { n: "1", icon: <UserCircle2 className="w-5 h-5" />,     t: "Understand the Whole Person",  b: "Identify needs, risks, strengths, circumstances, and transition priorities across multiple areas of life." },
              { n: "2", icon: <Compass className="w-5 h-5" />,         t: "Determine the Appropriate Pathway", b: "WHO Guides™ helps interpret the individual's needs, eligibility, location, preferences, and urgency." },
              { n: "3", icon: <LinkIcon className="w-5 h-5" />,        t: "Complete the Warm Handoff",     b: "WHO connects the individual directly to an appropriate verified organization or professional instead of providing another disconnected list of resources." },
              { n: "4", icon: <ActivityIcon className="w-5 h-5" />,    t: "Track What Happens Next",       b: "WHO records referral acceptance, follow-up activity, service gaps, barriers, and measurable outcomes." },
            ].map((step, i, arr) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="relative p-6 bg-white border border-borderSubtle rounded-lg"
                data-testid={`pennstate-step-${step.n}`}
              >
                {i < arr.length - 1 && (
                  <span className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 w-5 h-5 rounded-full bg-gold text-navy items-center justify-center" aria-hidden>
                    <ArrowRight className="w-3 h-3" strokeWidth={3} />
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-navy text-cream font-display font-black text-sm">{step.n}</span>
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gold/15 text-gold-deep">{step.icon}</span>
                </div>
                <h3 className="mt-4 font-display font-bold text-navy text-base leading-tight">{step.t}</h3>
                <p className="mt-2 text-sm text-slateSoft leading-relaxed">{step.b}</p>
              </motion.div>
            ))}
          </div>

          {/* Highlighted callout */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mt-14 rounded-2xl p-8 lg:p-10 bg-navy-deep text-cream text-center border border-gold/40"
            data-testid="pennstate-callout"
          >
            <p className="font-display font-black text-cream text-3xl lg:text-4xl leading-tight">
              Assessment should not end with a recommendation.<br />
              <span className="text-gold">It should begin a coordinated response.</span>
            </p>
            <p className="mt-4 text-cream/85 text-base lg:text-lg max-w-3xl mx-auto leading-relaxed">
              Penn State&apos;s research helps demonstrate how individualized needs can be identified.
              <span className="text-cream font-semibold"> WHO helps turn those insights into accountable action.</span>
            </p>
          </motion.div>

          {/* Two-column comparison */}
          <div className="mt-14 grid lg:grid-cols-2 gap-4" data-testid="pennstate-comparison">
            <div className="p-8 bg-white border border-borderSubtle rounded-lg">
              <p className="text-[10px] uppercase tracking-widest text-gold-deep font-bold">Evidence-Based Assessment</p>
              <h3 className="mt-2 font-display font-bold text-navy text-2xl leading-tight">Understanding the person</h3>
              <ul className="mt-6 space-y-3 text-navy/85">
                {[
                  "Identifies interconnected transition needs",
                  "Recognizes that veterans are not a monolithic population",
                  "Considers employment, education, finances, health, and relationships",
                  "Supports individualized recommendations",
                  "Helps organizations understand where assistance may be needed",
                ].map((li) => (
                  <li key={li} className="flex gap-3 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-gold-deep mt-1 shrink-0" />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-8 bg-navy text-cream rounded-lg relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gold/10" aria-hidden />
              <div className="relative">
                <p className="text-[10px] uppercase tracking-widest text-gold font-bold">WHO Coordination Infrastructure</p>
                <h3 className="mt-2 font-display font-bold text-cream text-2xl leading-tight">Completing the connection</h3>
                <ul className="mt-6 space-y-3 text-cream/90">
                  {[
                    "Matches needs with verified providers",
                    "Uses geography, eligibility, urgency, and preferences",
                    "Initiates direct warm handoffs",
                    "Assigns responsibility for the next step",
                    "Tracks referral status, follow-up, barriers, and outcomes",
                  ].map((li) => (
                    <li key={li} className="flex gap-3 leading-relaxed">
                      <LinkIcon className="w-4 h-4 text-gold mt-1 shrink-0" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* From Research to Coordinated Action — connects Penn State + RAND */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mt-14 p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-gold/15 to-gold/5 border border-gold/40"
            data-testid="research-synthesis"
          >
            <p className="eyebrow text-gold-deep">From Research to Coordinated Action</p>
            <p className="mt-4 text-navy leading-relaxed text-lg">
              RAND research highlights the need for stronger coordination, proactive warm handoffs,
              trusted resources, family inclusion, and common outcome measurement. Penn State research
              strengthens the case for individualized, whole-person assessment. Together, these
              findings reinforce WHO&apos;s operating model:
            </p>
            <p className="mt-6 font-display font-black text-navy text-2xl lg:text-3xl leading-tight">
              Understand the person. <span className="text-gold-deep">Identify the need.</span>
              &nbsp;Coordinate the response. <span className="text-gold-deep">Confirm the connection.</span>
            </p>
          </motion.div>

          {/* Disclosure, citation, CTA */}
          <div className="mt-10 pt-8 border-t border-borderSubtle grid md:grid-cols-2 gap-6 items-start">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gold-deep font-bold">Disclosure</p>
              <p className="mt-2 text-xs text-slateSoft leading-relaxed">
                WHO does not perform clinical diagnosis. Penn State and its researchers have not
                evaluated, partnered with, funded, or endorsed WHO. This section reflects WHO&apos;s
                proposed application of principles drawn from publicly available research.
              </p>
              <p className="mt-4 text-[10px] uppercase tracking-widest text-gold-deep font-bold">Source</p>
              <p className="mt-2 text-xs text-slateSoft leading-relaxed" data-testid="pennstate-citation">
                Penn State Social Science Research Institute, <em>&ldquo;New screener offers empirical
                insights to improve veteran transition support.&rdquo;</em>
              </p>
            </div>
            <div className="md:text-right">
              <a
                href="https://www.psu.edu/news/social-science-research-institute/story/new-screener-offers-empirical-insights-improve-veteran"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pennstate-review-btn"
                className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-cream font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                Review the Penn State Research
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </a>
              <p className="mt-3 text-[10px] uppercase tracking-widest text-slateSoft/60">Opens on psu.edu</p>
            </div>
          </div>
        </div>
      </section>

      {/* Learning from Established Veteran-Service Models (IVMF) */}
      <section
        className="relative py-24 lg:py-32 bg-navy-deep text-cream border-y border-white/5 overflow-hidden"
        data-testid="ivmf-learning-section"
        id="learning-from-ecosystem"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full bg-gold/8 blur-3xl" />
          <div className="absolute inset-0 dot-grid opacity-[0.06]" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          {/* Header */}
          <div className="max-w-3xl">
            <p className="eyebrow text-gold" data-testid="ivmf-eyebrow">
              Learning from Established Veteran-Service Models
            </p>
            <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.2rem]">
              WHO is being shaped by research, proven models, and lessons from the
              <span className="text-gold"> veteran-serving ecosystem</span>.
            </h2>
            <p className="mt-6 text-lg text-cream/85 leading-relaxed">
              The D&rsquo;Aniello Institute for Veterans and Military Families at Syracuse
              University, known as <span className="text-cream font-semibold">IVMF</span>, has
              developed respected research, analytics, entrepreneurship, career-transition, and
              community-coordination initiatives serving veterans and military families.
            </p>
            <p className="mt-4 text-cream/80 leading-relaxed">
              Its work provides important context for WHO&rsquo;s development, particularly its
              experience with coordinated community networks, cross-sector partnerships, transition
              support, data-informed service delivery, and veteran entrepreneurship.
            </p>
            <p className="mt-4 text-cream/80 leading-relaxed">
              WHO is studying these models to understand what has worked, where coordination gaps
              may remain, and how technology can strengthen{" "}
              <span className="text-cream font-semibold">accountability, personalization,
              follow-up, and outcomes</span>.
            </p>
          </div>

          {/* Focus areas — small icon chip row */}
          <div className="mt-10 flex flex-wrap gap-2" data-testid="ivmf-focus-areas">
            {[
              { icon: <BookOpen className="w-3.5 h-3.5" />, label: "Research" },
              { icon: <HeartHandshake className="w-3.5 h-3.5" />, label: "Coordinated care" },
              { icon: <Briefcase className="w-3.5 h-3.5" />, label: "Entrepreneurship" },
              { icon: <GraduationCap className="w-3.5 h-3.5" />, label: "Career transition" },
              { icon: <Database className="w-3.5 h-3.5" />, label: "Data & analytics" },
              { icon: <Globe className="w-3.5 h-3.5" />, label: "Community networks" },
            ].map((c) => (
              <span
                key={c.label}
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/15 text-cream/80"
                data-testid={`ivmf-focus-${c.label.toLowerCase().replace(/\W+/g, "-")}`}
              >
                <span className="text-gold">{c.icon}</span>
                {c.label}
              </span>
            ))}
          </div>

          {/* Subtle network diagram */}
          <div className="mt-14" data-testid="ivmf-network-diagram">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 lg:p-10">
              <div className="flex items-center justify-between mb-6">
                <p className="text-[10px] uppercase tracking-widest text-gold font-bold">
                  Coordinated ecosystem &mdash; conceptual view
                </p>
                <p className="text-[10px] uppercase tracking-widest text-cream/50">
                  Veterans &amp; families &harr; multiple service providers
                </p>
              </div>
              <svg
                viewBox="0 0 900 360"
                className="w-full h-auto"
                role="img"
                aria-label="Conceptual network diagram: a veteran and family at the center connected to six categories of service providers — research, career transition, community networks, healthcare, entrepreneurship, and data-informed services."
              >
                <defs>
                  <radialGradient id="ivmfCenterGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#C6934C" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#C6934C" stopOpacity="0" />
                  </radialGradient>
                  <linearGradient id="ivmfEdge" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#C6934C" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#DDB27C" stopOpacity="0.2" />
                  </linearGradient>
                </defs>

                {/* Center glow */}
                <circle cx="450" cy="160" r="140" fill="url(#ivmfCenterGlow)" />

                {/* Connection lines */}
                {[
                  { x: 120, y: 80 },
                  { x: 780, y: 80 },
                  { x: 90, y: 200 },
                  { x: 810, y: 200 },
                  { x: 240, y: 280 },
                  { x: 660, y: 280 },
                ].map((p, i) => (
                  <line
                    key={i}
                    x1="450"
                    y1="160"
                    x2={p.x}
                    y2={p.y}
                    stroke="url(#ivmfEdge)"
                    strokeWidth="1.2"
                    strokeDasharray="4 5"
                  />
                ))}

                {/* Outer provider nodes */}
                {[
                  { x: 120, y: 80,  label: "Research" },
                  { x: 780, y: 80,  label: "Career Transition" },
                  { x: 90,  y: 200, label: "Community Networks" },
                  { x: 810, y: 200, label: "Healthcare" },
                  { x: 240, y: 280, label: "Entrepreneurship" },
                  { x: 660, y: 280, label: "Data & Analytics" },
                ].map((n) => (
                  <g key={n.label}>
                    <circle
                      cx={n.x}
                      cy={n.y}
                      r="22"
                      fill="#0F2138"
                      stroke="#C6934C"
                      strokeOpacity="0.5"
                      strokeWidth="1.5"
                    />
                    <circle cx={n.x} cy={n.y} r="4" fill="#C6934C" />
                    <text
                      x={n.x}
                      y={n.y + 44}
                      textAnchor="middle"
                      fill="#F6F1E6"
                      fillOpacity="0.85"
                      style={{ fontFamily: "IBM Plex Sans, sans-serif", fontSize: "12px", letterSpacing: "0.05em" }}
                    >
                      {n.label}
                    </text>
                  </g>
                ))}

                {/* Center — veterans & families */}
                <circle cx="450" cy="160" r="46" fill="#0A1928" stroke="#C6934C" strokeWidth="2" />
                <circle cx="450" cy="160" r="46" fill="none" stroke="#DDB27C" strokeOpacity="0.35" strokeWidth="1" strokeDasharray="3 4" />
                <text
                  x="450"
                  y="156"
                  textAnchor="middle"
                  fill="#DDB27C"
                  style={{ fontFamily: "Chivo, sans-serif", fontWeight: 900, fontSize: "13px", letterSpacing: "0.14em" }}
                >
                  VETERANS
                </text>
                <text
                  x="450"
                  y="174"
                  textAnchor="middle"
                  fill="#F6F1E6"
                  style={{ fontFamily: "Chivo, sans-serif", fontWeight: 900, fontSize: "13px", letterSpacing: "0.14em" }}
                >
                  &amp; FAMILIES
                </text>
              </svg>
              <p className="mt-6 text-xs text-cream/50 leading-relaxed max-w-3xl">
                Conceptual illustration only. WHO&rsquo;s design draws on lessons from established
                coordinated-network models across research, career transition, community
                organizations, healthcare, entrepreneurship, and data-informed service delivery.
              </p>
            </div>
          </div>

          {/* Learning Flow — how WHO absorbs ecosystem lessons */}
          <div className="mt-10" data-testid="ivmf-learning-flow">
            <div className="grid md:grid-cols-3 gap-4 items-stretch relative">
              {/* connecting arrows visible on md+ */}
              <div className="hidden md:block absolute top-1/2 left-[33%] -translate-y-1/2 -translate-x-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-gold" strokeWidth={2.5} />
              </div>
              <div className="hidden md:block absolute top-1/2 left-[66%] -translate-y-1/2 -translate-x-1/2 z-10">
                <ArrowRight className="w-6 h-6 text-gold" strokeWidth={2.5} />
              </div>

              {[
                {
                  step: "01",
                  icon: (
                    <svg viewBox="0 0 64 64" className="w-14 h-14" aria-hidden>
                      <circle cx="32" cy="32" r="30" fill="none" stroke="#C6934C" strokeOpacity="0.35" strokeWidth="1.2" strokeDasharray="3 3" />
                      <rect x="16" y="18" width="32" height="26" rx="2" fill="none" stroke="#DDB27C" strokeWidth="1.6" />
                      <line x1="20" y1="24" x2="44" y2="24" stroke="#C6934C" strokeWidth="1.4" />
                      <line x1="20" y1="30" x2="40" y2="30" stroke="#C6934C" strokeOpacity="0.7" strokeWidth="1.4" />
                      <line x1="20" y1="36" x2="42" y2="36" stroke="#C6934C" strokeOpacity="0.5" strokeWidth="1.4" />
                      <circle cx="48" cy="48" r="6" fill="#0F2138" stroke="#C6934C" strokeWidth="1.4" />
                      <path d="M46 48h4M48 46v4" stroke="#DDB27C" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                  ),
                  title: "Study",
                  b: "Examine research, coordinated-network experience, transition programs, entrepreneurship models, and family-support frameworks already in the ecosystem.",
                  tag: "Research & benchmarking",
                },
                {
                  step: "02",
                  icon: (
                    <svg viewBox="0 0 64 64" className="w-14 h-14" aria-hidden>
                      <circle cx="32" cy="32" r="30" fill="none" stroke="#C6934C" strokeOpacity="0.35" strokeWidth="1.2" strokeDasharray="3 3" />
                      <path d="M22 18l4 4M42 18l-4 4M22 46l4-4M42 46l-4-4" stroke="#C6934C" strokeOpacity="0.6" strokeWidth="1.4" strokeLinecap="round" />
                      <circle cx="32" cy="32" r="12" fill="none" stroke="#DDB27C" strokeWidth="1.6" />
                      <path d="M32 22v20M22 32h20" stroke="#C6934C" strokeWidth="1.3" strokeLinecap="round" />
                      <circle cx="32" cy="32" r="3" fill="#C6934C" />
                    </svg>
                  ),
                  title: "Extract Lessons",
                  b: "Identify what already works — and where coordination, personalization, follow-through, and accountability can be strengthened.",
                  tag: "Insight synthesis",
                },
                {
                  step: "03",
                  icon: (
                    <svg viewBox="0 0 64 64" className="w-14 h-14" aria-hidden>
                      <circle cx="32" cy="32" r="30" fill="none" stroke="#C6934C" strokeOpacity="0.35" strokeWidth="1.2" strokeDasharray="3 3" />
                      <path
                        d="M20 40c0-8 6-14 12-14s12 6 12 14"
                        fill="none"
                        stroke="#DDB27C"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                      <circle cx="20" cy="40" r="3" fill="#C6934C" />
                      <circle cx="32" cy="26" r="3" fill="#C6934C" />
                      <circle cx="44" cy="40" r="3" fill="#C6934C" />
                      <path d="M32 26v-6M28 22l4-4 4 4" stroke="#C6934C" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                  title: "Apply via WHO Guides",
                  b: "Translate lessons into voice-first navigation, warm handoffs, closed-loop follow-up, and measurable outcomes — powered by human-in-the-loop AI.",
                  tag: "Design & delivery",
                },
              ].map((s, i) => (
                <motion.div
                  key={s.step}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="relative p-6 rounded-xl bg-white/[0.04] border border-white/10 hover:border-gold/40 transition-colors"
                  data-testid={`ivmf-flow-step-${s.step}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-display font-black text-gold text-2xl leading-none">{s.step}</span>
                    <div className="shrink-0">{s.icon}</div>
                  </div>
                  <p className="mt-4 text-[10px] uppercase tracking-widest text-gold-light font-bold">{s.tag}</p>
                  <h4 className="mt-2 font-display font-bold text-cream text-xl leading-tight">{s.title}</h4>
                  <p className="mt-2 text-cream/70 text-sm leading-relaxed">{s.b}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Two-column comparison */}
          <div className="mt-14 grid lg:grid-cols-2 gap-4" data-testid="ivmf-comparison">
            <div className="p-8 lg:p-10 bg-white/[0.04] border border-white/10 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white/[0.06] border border-white/15 text-cream">
                  <BookOpen className="w-4 h-4" />
                </span>
                <p className="text-[10px] uppercase tracking-widest text-cream/60 font-bold">
                  What Established Models Demonstrate
                </p>
              </div>
              <h3 className="mt-4 font-display font-bold text-cream text-2xl leading-tight">
                Coordination is possible &mdash; and instructive.
              </h3>
              <ul className="mt-6 space-y-3 text-cream/85" data-testid="ivmf-established-list">
                {[
                  { icon: <Network className="w-4 h-4" />,       t: "Communities can organize veteran-serving providers into coordinated networks." },
                  { icon: <Compass className="w-4 h-4" />,       t: "Veterans and military families benefit from centralized navigation and shared referral pathways." },
                  { icon: <Route className="w-4 h-4" />,         t: "Transition needs extend across employment, education, health, housing, finances, and family life." },
                  { icon: <Database className="w-4 h-4" />,      t: "Shared data can help identify gaps, improve services, and measure collective impact." },
                  { icon: <HeartHandshake className="w-4 h-4" />, t: "Cross-sector collaboration is essential to improving transition outcomes." },
                ].map((li) => (
                  <li key={li.t} className="flex gap-3 leading-relaxed items-start">
                    <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/[0.05] border border-white/10 text-cream/70 shrink-0">
                      {li.icon}
                    </span>
                    <span className="pt-1">{li.t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-8 lg:p-10 bg-gradient-to-br from-gold/12 to-gold/[0.03] border border-gold/40 rounded-lg relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gold/10" aria-hidden />
              <div className="relative">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-gold/20 border border-gold/40 text-gold">
                    <Sparkles className="w-4 h-4" />
                  </span>
                  <p className="text-[10px] uppercase tracking-widest text-gold font-bold">
                    What WHO Seeks to Advance
                  </p>
                </div>
                <h3 className="mt-4 font-display font-bold text-cream text-2xl leading-tight">
                  From coordinated networks to accountable connections.
                </h3>
                <ul className="mt-6 space-y-3 text-cream/90" data-testid="ivmf-who-list">
                  {[
                    { icon: <MessagesSquare className="w-4 h-4" />, t: "Voice-first and AI-supported navigation through WHO Guides™" },
                    { icon: <Route className="w-4 h-4" />,          t: "Personalized pathways based on needs, eligibility, geography, urgency, and preferences" },
                    { icon: <HeartHandshake className="w-4 h-4" />, t: "Direct warm handoffs rather than static referral lists" },
                    { icon: <UserPlus className="w-4 h-4" />,       t: "Clear ownership of the next step" },
                    { icon: <Bell className="w-4 h-4" />,           t: "Real-time follow-up, escalation, and referral-status visibility" },
                    { icon: <Users className="w-4 h-4" />,          t: "Family-centered coordination" },
                    { icon: <ShieldCheck className="w-4 h-4" />,    t: "Shared accountability across participating organizations" },
                    { icon: <BarChart3 className="w-4 h-4" />,      t: "Better visibility into unmet needs, barriers, and system gaps" },
                  ].map((li) => (
                    <li key={li.t} className="flex gap-3 leading-relaxed items-start">
                      <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md bg-gold/20 border border-gold/40 text-gold shrink-0">
                        {li.icon}
                      </span>
                      <span className="pt-1">{li.t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Highlighted statement 1 */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mt-14 rounded-2xl p-8 lg:p-10 bg-white/[0.04] border-l-4 border-gold"
            data-testid="ivmf-callout-1"
          >
            <p className="eyebrow text-gold-light">A stance of humility</p>
            <p className="mt-3 font-display font-black text-cream text-2xl lg:text-3xl leading-tight">
              WHO is not starting from the assumption that no one has tried coordination before.
              <span className="text-gold"> It is being built by learning from what already exists
              and identifying what can be improved.</span>
            </p>
          </motion.div>

          {/* Highlighted statement 2 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mt-6 rounded-2xl p-8 lg:p-10 bg-gradient-to-br from-gold/15 to-gold/5 border border-gold/40 text-center"
            data-testid="ivmf-callout-2"
          >
            <p className="font-display font-black text-cream text-3xl lg:text-4xl leading-tight">
              Established models show that <span className="text-gold">coordinated networks are possible</span>.
              <br className="hidden md:block" />
              WHO seeks to make those connections more{" "}
              <span className="text-gold">personalized, accountable, and measurable</span>.
            </p>
          </motion.div>

          {/* Why IVMF Matters to WHO */}
          <div className="mt-14 grid lg:grid-cols-12 gap-8 items-start" data-testid="ivmf-why-matters">
            <div className="lg:col-span-5">
              <p className="eyebrow text-gold-light">Why IVMF Matters to WHO</p>
              <h3 className="mt-3 font-display font-black text-cream text-2xl lg:text-3xl leading-tight">
                An important source of ecosystem learning &mdash; not a partner.
              </h3>

              {/* Illustrative "learning bridge" graphic */}
              <div className="mt-6 relative rounded-xl bg-white/[0.03] border border-white/10 p-5">
                <svg viewBox="0 0 320 140" className="w-full h-auto" aria-hidden>
                  <defs>
                    <linearGradient id="ivmfBridge" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#DDB27C" stopOpacity="0.15" />
                      <stop offset="50%" stopColor="#C6934C" stopOpacity="0.85" />
                      <stop offset="100%" stopColor="#DDB27C" stopOpacity="0.15" />
                    </linearGradient>
                  </defs>
                  {/* left pillar — Ecosystem */}
                  <g>
                    <rect x="20" y="46" width="72" height="72" rx="4" fill="none" stroke="#DDB27C" strokeOpacity="0.55" strokeWidth="1.4" />
                    <line x1="30" y1="60" x2="82" y2="60" stroke="#C6934C" strokeOpacity="0.7" strokeWidth="1.2" />
                    <line x1="30" y1="72" x2="76" y2="72" stroke="#C6934C" strokeOpacity="0.5" strokeWidth="1.2" />
                    <line x1="30" y1="84" x2="80" y2="84" stroke="#C6934C" strokeOpacity="0.5" strokeWidth="1.2" />
                    <line x1="30" y1="96" x2="70" y2="96" stroke="#C6934C" strokeOpacity="0.5" strokeWidth="1.2" />
                    <text x="56" y="134" textAnchor="middle" fill="#F6F1E6" fillOpacity="0.85" style={{ fontFamily: "Chivo, sans-serif", fontWeight: 700, fontSize: "10px", letterSpacing: "0.12em" }}>
                      ECOSYSTEM
                    </text>
                  </g>
                  {/* right pillar — WHO Design */}
                  <g>
                    <rect x="228" y="46" width="72" height="72" rx="4" fill="none" stroke="#DDB27C" strokeOpacity="0.55" strokeWidth="1.4" />
                    <circle cx="264" cy="82" r="18" fill="none" stroke="#C6934C" strokeOpacity="0.7" strokeWidth="1.4" />
                    <circle cx="264" cy="82" r="4" fill="#C6934C" />
                    <path d="M264 64v-8M264 108v-8M246 82h-8M290 82h-8" stroke="#C6934C" strokeOpacity="0.6" strokeWidth="1.2" strokeLinecap="round" />
                    <text x="264" y="134" textAnchor="middle" fill="#F6F1E6" fillOpacity="0.85" style={{ fontFamily: "Chivo, sans-serif", fontWeight: 700, fontSize: "10px", letterSpacing: "0.12em" }}>
                      WHO DESIGN
                    </text>
                  </g>
                  {/* bridge — lessons flowing */}
                  <path d="M92 82 Q160 30 228 82" fill="none" stroke="url(#ivmfBridge)" strokeWidth="2.5" strokeLinecap="round" />
                  {/* lesson-flow dots */}
                  <circle cx="126" cy="60" r="2.5" fill="#DDB27C" />
                  <circle cx="160" cy="48" r="3" fill="#C6934C" />
                  <circle cx="194" cy="60" r="2.5" fill="#DDB27C" />
                  {/* label */}
                  <text x="160" y="24" textAnchor="middle" fill="#DDB27C" style={{ fontFamily: "Chivo, sans-serif", fontWeight: 900, fontSize: "9px", letterSpacing: "0.22em" }}>
                    LESSONS &amp; INSIGHTS
                  </text>
                </svg>
                <p className="mt-3 text-[10px] uppercase tracking-widest text-cream/40">
                  Illustration &mdash; conceptual only
                </p>
              </div>
            </div>
            <div className="lg:col-span-7 space-y-4 text-cream/85 text-lg leading-relaxed">
              <p>
                IVMF&rsquo;s research and analytics work, AmericaServes experience, military-family
                expertise, career-transition programs, and Military Founders Lab make it an
                important source of learning for WHO.
              </p>
              <p>
                WHO will continue to examine IVMF&rsquo;s work as part of its research,
                benchmarking, partnership-development, and founder-learning process.
              </p>

              {/* IVMF domain chips */}
              <div className="pt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ivmf-domain-chips">
                {[
                  { icon: <BookOpen className="w-4 h-4" />,        t: "Research & Analytics" },
                  { icon: <Globe className="w-4 h-4" />,           t: "Community Networks" },
                  { icon: <Users className="w-4 h-4" />,           t: "Military Families" },
                  { icon: <GraduationCap className="w-4 h-4" />,   t: "Career Transition" },
                  { icon: <Briefcase className="w-4 h-4" />,       t: "Entrepreneurship" },
                  { icon: <Lightbulb className="w-4 h-4" />,       t: "Founder Learning" },
                ].map((c) => (
                  <div
                    key={c.t}
                    className="flex items-center gap-2.5 p-3 rounded-md bg-white/[0.03] border border-white/10"
                    data-testid={`ivmf-domain-${c.t.toLowerCase().replace(/\W+/g, "-")}`}
                  >
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gold/15 border border-gold/30 text-gold shrink-0">
                      {c.icon}
                    </span>
                    <span className="text-cream/85 text-sm font-medium leading-tight">{c.t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Disclaimer + CTA */}
          <div className="mt-12 pt-8 border-t border-white/10 grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <p className="text-[10px] uppercase tracking-widest text-gold-light font-bold">Disclaimer</p>
              <p className="mt-2 text-xs text-cream/60 leading-relaxed" data-testid="ivmf-disclaimer">
                References to external organizations, programs, and research are provided for
                informational and benchmarking purposes and do not imply endorsement, funding,
                affiliation, or partnership.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <a
                href="https://ivmf.syracuse.edu/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ivmf-explore-btn"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                Explore IVMF
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </a>
              <p className="mt-3 text-[10px] uppercase tracking-widest text-cream/40">Opens on ivmf.syracuse.edu</p>
            </div>
          </div>
        </div>
      </section>

      {/* Referral vs Warm Handoff */}
      <Section eyebrow="Referral vs. Warm Handoff" title="One is information. The other is a connection." bg="cream">
        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          <div className="p-8 lg:p-10 bg-cream-panel border border-borderSubtle rounded-lg" data-testid="referral-column">
            <div className="flex items-center gap-3">
              <PhoneOff className="w-6 h-6 text-slateSoft" />
              <p className="eyebrow text-slateSoft">Traditional referral</p>
            </div>
            <h3 className="mt-4 font-display font-bold text-navy text-2xl leading-tight">
              &ldquo;Here&apos;s a number to call.&rdquo;
            </h3>
            <ul className="mt-6 space-y-3">
              {traditional.map((t, i) => (
                <li key={i} className="flex gap-3 text-navy/85 leading-relaxed" data-testid={`traditional-item-${i}`}>
                  <Unlink className="w-4 h-4 text-slateSoft mt-1.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="p-8 lg:p-10 bg-navy text-cream rounded-lg relative overflow-hidden" data-testid="warm-column">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gold/10" aria-hidden />
            <div className="relative">
              <div className="flex items-center gap-3">
                <LinkIcon className="w-6 h-6 text-gold" />
                <p className="eyebrow text-gold-light">WHO warm handoff</p>
              </div>
              <h3 className="mt-4 font-display font-bold text-cream text-2xl leading-tight">
                &ldquo;I&apos;m connecting you directly — and we&apos;ll confirm it happened.&rdquo;
              </h3>
              <ul className="mt-6 space-y-3">
                {warmHandoff.map((t, i) => (
                  <li key={i} className="flex gap-3 text-cream/90 leading-relaxed" data-testid={`warm-item-${i}`}>
                    <CheckCircle2 className="w-4 h-4 text-gold mt-1.5 shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <p className="mt-8 font-display font-bold text-navy text-center text-xl" data-testid="referral-vs-footer">
          Resources exist. WHO makes the connection accountable.
        </p>
      </Section>

      {/* Founder insight quote */}
      <Section>
        <div className="max-w-4xl mx-auto text-center" data-testid="founder-insight">
          <p className="eyebrow text-gold">Founder insight</p>
          <p className="mt-6 font-display font-black text-navy text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
            &ldquo;The problem is not that help does not exist.<br />
            <span className="text-gold">The problem is that too many people are left alone</span><br />
            between the referral and the real connection.&rdquo;
          </p>
          <p className="mt-8 text-slateSoft">— Marlon L. Ferguson, Founder & CEO · Warm Hand Off LLC</p>
        </div>
      </Section>

      {/* GAO Direct Quote — validates WHO's mission */}
      <section className="py-24 lg:py-32 hero-gradient text-cream" data-testid="gao-quote-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <p className="eyebrow text-gold-light">Federal validation</p>
              <p className="mt-4 font-display font-black text-cream text-2xl lg:text-3xl leading-tight">
                The U.S. Government Accountability Office has identified the exact gap WHO exists to close.
              </p>
              <a
                href="https://www.gao.gov/products/gao-24-106248"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gao-report-link"
                className="mt-6 inline-flex items-center gap-2 text-gold hover:text-gold-light transition-colors text-sm font-semibold"
              >
                Read GAO-24-106248
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 3H21m0 0v7.5M21 3l-7.5 7.5M10.5 3H3v18h18v-7.5" />
                </svg>
              </a>
            </div>

            <div className="lg:col-span-8 relative">
              <svg
                className="absolute -top-4 -left-2 w-16 h-16 text-gold/25"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden
              >
                <path d="M6.4 4C3.4 4 1 6.4 1 9.4v10.6h9.4V9.4H4c0-2.6 1.8-4.4 4.4-4.4V4H6.4zM17 4c-3 0-5.4 2.4-5.4 5.4v10.6h9.4V9.4h-6.4c0-2.6 1.8-4.4 4.4-4.4V4H17z" />
              </svg>
              <blockquote className="relative pl-8" data-testid="gao-blockquote">
                <p className="font-display font-black text-cream text-2xl md:text-3xl lg:text-4xl leading-[1.15]">
                  Through its Transition Assistance Program, DOD ensures these service members
                  receive agency contact information, but <span className="text-gold">does not ensure that a connection with a person actually occurs</span>.
                </p>
                <footer className="mt-8 text-cream/80 text-sm leading-relaxed">
                  <span className="text-gold-light font-semibold">GAO-24-106248</span> · Service Members Transitioning to Civilian Life: Agencies Can Improve Warm Handovers for Additional Assistance · Published March 21, 2024
                </footer>
              </blockquote>

              <div className="mt-10 grid sm:grid-cols-2 gap-6">
                <div className="p-6 border border-white/10 rounded-lg bg-white/[0.04]" data-testid="gao-finding-1">
                  <p className="font-display font-black text-gold text-4xl leading-none">41,000</p>
                  <p className="mt-3 text-cream/90 text-sm leading-relaxed">
                    Warm handovers DOD provided to at-risk service members (Apr 2021 – Mar 2023).
                  </p>
                </div>
                <div className="p-6 border border-white/10 rounded-lg bg-white/[0.04]" data-testid="gao-finding-2">
                  <p className="font-display font-black text-gold text-4xl leading-none">4,300+</p>
                  <p className="mt-3 text-cream/90 text-sm leading-relaxed">
                    At-risk service members who did NOT receive a warm handover in that same period — with no analysis of why.
                  </p>
                </div>
              </div>

              <p className="mt-8 text-cream/80 leading-relaxed">
                GAO also found that DOD had not assessed whether warm handovers are actually helpful.
                WHO was built to close exactly that gap — with verified matches, confirmed handoffs,
                follow-up tracking, and measurable outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bridge to solution + sources */}
      <Section eyebrow="Why this matters" title="Human-centered handoffs are a systems-design problem — and a solvable one." bg="cream">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-5 text-lg text-slateSoft leading-relaxed">
            <p>
              None of this is a failure of DoD, VA, or community providers acting in good faith. It is a
              failure of the connective tissue between them — the absent infrastructure for verified matches,
              consent-based data sharing, confirmed handoffs, continuity of care, and outcome tracking.
            </p>
            <p>
              That is the gap WHO closes. Not another program. Not another app. WHO is the
              Relationship Operating System&trade; for Human Services — agentic by design, so a
              person can speak one need out loud, and WHO Guides&trade; (one real Guide plus nine
              specialist AI agents) make the warm handoff, coordinate with partners, and stay
              in the loop until the outcome is real.
            </p>
            <p className="text-navy font-medium">
              WHO turns fragmented referrals into accountable, measurable, human-centered connections —
              with WHO Guides as the front door.
            </p>
            <div className="pt-4">
              <a
                href="/solution"
                data-testid="problem-to-solution-link"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                See how WHO closes the gap <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </a>
            </div>
          </div>

          <aside className="lg:col-span-5" data-testid="sources-list">
            <p className="eyebrow text-gold-deep">Sources · verify directly</p>
            <ul className="mt-4 space-y-2 text-sm">
              {sources.map((s, i) => (
                <li key={s.name}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`source-link-${i}`}
                    className="group flex items-start gap-2 text-navy hover:text-gold-deep transition-colors leading-snug"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mt-1 shrink-0 opacity-70" />
                    <span className="underline decoration-borderSubtle group-hover:decoration-gold underline-offset-4">
                      {s.name}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </Section>

      <CtaBanner
        eyebrow="A better way exists."
        title="See how WHO turns referrals into real connections."
        primaryText="See the Solution"
        primaryTo="/solution"
        secondaryText="Meet WHO Guides"
        secondaryTo="/platform"
      />
    </div>
  );
}

// 10 program icon positions previously used for the flicker-mask overlay
// (removed per design decision — kept here as reference in case the effect returns).
// If not reintroduced, this block can be deleted.

